'use client';

import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {isAdvisorType} from '@zoefin/utils';

import {
  AddToDataLayer,
  DataLayer,
  DataLayerContextState,
  DataLayerEvent,
  DataLayerProviderProps,
} from './types';

const missingProvider = 'You forgot to wrap your app in <DataLayerProvider>';

export const DataLayerContext = createContext<DataLayerContextState>({
  get addToDataLayer(): never {
    throw new Error(missingProvider);
  },
  get gtag(): never {
    throw new Error(missingProvider);
  },
});

export const DataLayerProvider: FC<
  PropsWithChildren<DataLayerProviderProps>
> = ({user, children}) => {
  const [queue, setQueue] = useState<Record<string, DataLayerEvent>>({});
  const [currentUser, setUser] = useState<Advisor | Prospect>(user || null);

  const dequeue = (key: number) => {
    setQueue((s) => {
      const copy = {...s};

      delete copy[key];

      return copy;
    });
  };

  const addToDataLayer: AddToDataLayer = useCallback((event, values) => {
    const newEntry = {event, values};

    setQueue((s) => ({...s, [Object.keys(s).length]: newEntry}));
  }, []);

  /**
   * Wrapper for `window.gtag` method. Globally defined in `_app`
   */
  function gtag(...args: any[]) {
    window.gtag =
      window.gtag ||
      function gtagBackup(...argsBackup: any[]) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(...argsBackup);
      };

    window.gtag(...args);
  }

  const dataLayer: DataLayer = ({event, values}) => {
    if (window) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event,
        ...(values || {}),
      });
    }
  };

  useEffect(() => {
    const keys = Object.keys(queue);

    if (currentUser && keys.length) {
      const intKey = parseInt(keys[0], 10);
      const company = isAdvisorType(currentUser)
        ? currentUser.company
        : undefined;

      dataLayer({
        event: queue[intKey].event,
        values: {
          ...queue[intKey].values,
          User_Id: currentUser.id,
          User_Role: currentUser.role,
          User_Name: currentUser.full_name,
          User_Company: company?.name,
        },
      });
      dequeue(intKey);
    }
  }, [currentUser, queue]);

  const extendedReducer = useMemo<DataLayerContextState>(() => {
    return {
      setUser,
      addToDataLayer,
      gtag,
    };
  }, [addToDataLayer]);

  return (
    <DataLayerContext.Provider value={extendedReducer}>
      {children}
    </DataLayerContext.Provider>
  );
};

export const useDataLayer = (): DataLayerContextState =>
  useContext<DataLayerContextState>(DataLayerContext);
